import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Palette } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { DriverColorPicker } from "../components/routes/DriverColorPicker";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";

export default function DriverColors() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, items: [] });
  const [regenerating, setRegenerating] = useState(null);
  const [palette, setPalette] = useState([]);
  const [pickerDriver, setPickerDriver] = useState(null);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    apiClient
      .get("/drivers", { params: { limit: 200 } })
      .then(({ data }) => setState({ loading: false, items: data.items.filter((d) => !d.is_deleted) }))
      .catch((error) => {
        toast.error(getApiErrorMessage(error, "Could not load drivers."));
        setState({ loading: false, items: [] });
      });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiClient.get("/drivers/color-palette").then(({ data }) => setPalette(data.colors)).catch(() => setPalette([]));
  }, []);

  const regenerate = async (id) => {
    setRegenerating(id);
    try {
      await apiClient.post(`/drivers/${id}/color/regenerate`);
      toast.success("Color updated");
      load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not regenerate this driver's color."));
    } finally {
      setRegenerating(null);
    }
  };

  const pickColor = async (hex) => {
    try {
      await apiClient.put(`/drivers/${pickerDriver.id}/color`, { color: hex });
      toast.success(`${pickerDriver.name}'s color updated`);
      setPickerDriver(null);
      load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not set this color."));
    }
  };

  const usedColors = new Set(
    state.items.filter((d) => pickerDriver && d.id !== pickerDriver.id && d.color).map((d) => d.color.toUpperCase())
  );

  return (
    <div data-testid="driver-colors-page">
      <PageHeader
        title="Driver Colors"
        description="Each driver's route/marker color, kept consistent everywhere. Green is reserved exclusively for completed stops."
        actions={
          <Button variant="secondary" onClick={() => navigate("/drivers")} data-testid="driver-colors-back-button">
            <ArrowLeft size={16} strokeWidth={1.5} /> Back to Drivers
          </Button>
        }
      />

      {state.loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="driver-colors-grid">
          {state.items.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between rounded-md p-4 text-white shadow-sm"
              style={{ background: d.color || "#6B7280" }}
              data-testid={`driver-color-card-${d.id}`}
            >
              <div>
                <p className="font-semibold">{d.name}</p>
                <p className="text-xs opacity-80">#{d.id}{d.phone ? ` · ${d.phone}` : ""}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPickerDriver(d)}
                  className="rounded-full bg-white/20 p-2 hover:bg-white/30"
                  title="Pick a color"
                  data-testid={`driver-color-pick-${d.id}`}
                >
                  <Palette size={15} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  onClick={() => regenerate(d.id)}
                  disabled={regenerating === d.id}
                  className="rounded-full bg-white/20 p-2 hover:bg-white/30 disabled:opacity-50"
                  title="Randomize color"
                  data-testid={`driver-color-regenerate-${d.id}`}
                >
                  <RefreshCw size={15} strokeWidth={2} className={regenerating === d.id ? "animate-spin" : ""} />
                </button>
              </div>
            </div>
          ))}
          {state.items.length === 0 && (
            <p className="text-sm text-text-muted" data-testid="driver-colors-empty">No active drivers yet.</p>
          )}
        </div>
      )}

      <DriverColorPicker
        open={!!pickerDriver}
        onOpenChange={(v) => !v && setPickerDriver(null)}
        driverName={pickerDriver?.name}
        colors={palette}
        currentColor={pickerDriver?.color}
        usedColors={usedColors}
        onSelect={pickColor}
      />
    </div>
  );
}

